"use client";

import React, { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent } from "@/components/ui/card";
function AnimatedCounter({
  end,
  suffix = "",
  duration = 2000
}: {
  end: number;
  suffix?: string;
  duration?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLSpanElement>({
    once: true,
    threshold: 0.5
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTime: number | null = null;
    let animationFrameId: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      const easeOut = percentage === 1 ? 1 : 1 - Math.pow(2, -10 * percentage);
      setCount(Math.floor(end * easeOut));
      if (progress < duration) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isVisible, end, duration]);
  return <span ref={ref}>
      {count.toLocaleString()}
      {suffix}
    </span>;
}
export default function OurGrowingCommunity() {
  const stats = [{
    label: "Active Users",
    value: 50000,
    suffix: "+"
  }, {
    label: "Partner Venues",
    value: 500,
    suffix: "+"
  }, {
    label: "Charges Delivered",
    value: 1000000,
    suffix: "+"
  }];
  const marqueeItems = [{
    id: 1,
    type: "quote",
    quote: "ZapGO! made sure I always had a charged phone during my busy day. Highly recommended!",
    author: "Jan de Vries",
    role: "End-user"
  }, {
    id: 2,
    type: "logo",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/partner_cafe_de_hoek.png",
    alt: "Café De Hoek"
  }, {
    id: 3,
    type: "quote",
    quote: "Hosting a ZapGO! station has brought more customers to our café and extra revenue.",
    author: "Café De Hoek",
    role: "Venue Owner"
  }, {
    id: 4,
    type: "logo",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/partner_tech_hub.png",
    alt: "Tech Hub"
  }, {
    id: 5,
    type: "quote",
    quote: "Fast, reliable, and everywhere I need it to be. The app is super easy to use.",
    author: "Sarah M.",
    role: "Student"
  }, {
    id: 6,
    type: "logo",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/partner_central_station.png",
    alt: "Central Station"
  }];
  const duplicatedMarqueeItems = [...marqueeItems, ...marqueeItems];
  return <section id="onze-groeiende-community" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 mb-20 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Our Growing Community
          </h2>
          <p className="text-lg text-muted-foreground">
            Join thousands of users and venues across the Netherlands who rely on <span className="text-[#ffe838]">Zap</span><span className="text-[#70cf70]">GO!</span> to stay connected and powered up.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {stats.map((stat, i) => <div key={i} data-index={i} className="flex flex-col items-center justify-center p-8 rounded-3xl bg-card/40 border border-border/50 backdrop-blur-md shadow-lg transition-transform hover:-translate-y-1 duration-300">
              <div className="text-5xl md:text-6xl font-mono font-bold text-primary mb-4 tracking-tighter drop-shadow-[0_0_15px_rgba(var(--color-primary),0.3)]">
                <AnimatedCounter end={stat.value} suffix={stat.suffix} />
              </div>
              <div className="text-base md:text-lg text-muted-foreground font-medium uppercase tracking-wider">
                {stat.label}
              </div>
            </div>)}
        </div>
      </div>

      {}
      <div className="relative w-full max-w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_15%,black_85%,transparent)] flex items-center py-4 z-10">
        <div className="flex w-max animate-marquee hover:[animation-play-state:paused]">
          {duplicatedMarqueeItems.map((item, i) => <div key={i} data-index={i} className="mx-4 flex-shrink-0 flex items-center justify-center">
              {item.type === "quote" ? <Card className="w-[320px] bg-card/60 backdrop-blur-xl border-border/40 shadow-xl h-full">
                  <CardContent className="p-6 flex flex-col justify-between h-full gap-4">
                    <p className="text-sm text-foreground/90 leading-relaxed italic">
                      "
                      {item.quote.split(/(Zap|GO!)/g).map((part, idx) => {
                  if (part === "Zap") return <span key={idx} className="text-[#ffe838]">Zap</span>;
                  if (part === "GO!") return <span key={idx} className="text-[#70cf70]">GO!</span>;
                  return part;
                })}
                      "
                    </p>
                    <div className="flex flex-col mt-2">
                      <span className="text-sm font-bold text-primary">
                        {item.author}
                      </span>
                      <span className="text-xs text-muted-foreground font-medium">
                        {item.role}
                      </span>
                    </div>
                  </CardContent>
                </Card> : <div className="w-[200px] h-[120px] bg-card/20 rounded-2xl border border-border/20 flex items-center justify-center p-6 backdrop-blur-sm transition-all duration-500 hover:bg-card/40 hover:border-border/40 group">
                  <img src={item.src} alt={item.alt} className="max-w-full max-h-full object-contain opacity-50 grayscale group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500" />
                </div>}
            </div>)}
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
      __html: `
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee {
            animation: marquee 45s linear infinite;
          }
        `
    }} />
    </section>;
}